import { useEffect, useRef, useState } from "react";
import {
  CAPTION_MAX_GRAPHEMES,
  CAPTION_OVERLAY_H,
  VIDEO_PHOTO_H,
  captionLength,
  isValidCaption,
  normalizeCaption,
  truncateCaption,
  videoAspectCss,
} from "@fiket/booth-schema";
import { useRouter } from "../../store/router";
import { useBoothPayment } from "../../store/booth";
import { useI18n } from "../../i18n/useI18n";
import { BackButton } from "../../components/ui/BackButton";
import { Button } from "../../components/ui/Button";
import { afterCaptionRoute, captionBackRoute } from "./videoFlow";
import { renderCaptionPng } from "./captionCanvas";
import { anyPaymentActive } from "../Payment/paymentFlow";
import { useCaptionStyle } from "../../hooks/useStyles";


/** 자막 영역 비율 — 3:4 사진 높이 중 하단 오버레이가 차지하는 몫 = 1/4 */
const OVERLAY_RATIO = CAPTION_OVERLAY_H / VIDEO_PHOTO_H;

/** 미리보기 PNG 디바운스 — 입력이 끝나고 120ms 뒤 렌더 */
const PREVIEW_DEBOUNCE_MS = 120;

/**
 * STAGE · VideoCaption (HERIT 캔버스 9.3 "한 줄을 남기시겠어요?", BOOTH-85) — 영상 안 하단에 겹칠 한 줄(최대 12자, grapheme).
 * 위: 3:4 미리보기(변환 사진 + 하단 1/4 그늘 위 1440×480 PNG, `renderCaptionPng`) — 서버 ffmpeg 결과와 같은 기하(`VIDEO_PHOTO_*`·`CAPTION_OVERLAY_*`).
 * 2026-10-19 까진 아래 종이색 띠(2:3) 였으나 "글자를 붙인 카드" 로 보여 영상 안 겹침으로 바꿨다.
 * 아래: 한 줄 입력칸(`n/12` 카운터) + 프리셋 pill 4개(탭 = 치환, 콘솔 문구 편집으로 부스별 교체). CTA "다음" 은 정규화 자막이 유효할 때만 —
 * 캔버스로 1440×480 투명 PNG(`renderCaptionPng`) 를 그려 store 에 두고 VideoGenerateView 가 본문에 싣는다. "넣지 않기" 는 자막을 비운다.
 * 둘 다 다음은 `afterCaptionRoute` — 결제가 걸리면 여기서 `beginPayment(true)` 후 결제 화면, 아니면 바로 생성. 결제 뒤엔 되돌아오지 않는다.
 * 뒤로 = 동작을 골랐으면 동작 선택, 아니면 소개. PNG 렌더가 실패하면 한 줄 안내 후 자막 없이 진행(영상을 자막 때문에 막지 않는다).
 * 자막 스타일은 `useCaptionStyle()` — 선택 테마 → 부스 → cinema 순. 스타일별로 서체·색·효과가 다름(BOOTH-85).
 */
export function VideoCaptionView() {
  const go = useRouter((s) => s.go);
  const selectedMotionIds = useRouter((s) => s.selectedMotionIds);
  const savedCaption = useRouter((s) => s.videoCaption);
  const setVideoCaption = useRouter((s) => s.setVideoCaption);
  const clearVideoCaption = useRouter((s) => s.clearVideoCaption);
  const beginPayment = useRouter((s) => s.beginPayment);
  const photo = useRouter((s) => s.capturedShots[0]?.transformedDataUrl ?? s.capturedShots[0]?.dataUrl ?? null);
  const pay = useBoothPayment();
  const { t, language } = useI18n();

  // 선택 테마 → 부스 → cinema 순으로 자막 스타일 해석 (BOOTH-85)
  const style = useCaptionStyle();

  // 뒤로 와서 다시 열면 이전 자막을 그대로 — 생성 화면에서 돌아온 경우
  const [text, setText] = useState(savedCaption ?? "");
  const [busy, setBusy] = useState(false);
  const [renderError, setRenderError] = useState(false);

  // 미리보기 PNG — 입력 120ms 후 디바운스 렌더 (1440×480 스타일별)
  const [previewPng, setPreviewPng] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const normalized = normalizeCaption(text);
  const valid = isValidCaption(normalized);

  // 텍스트·스타일·언어 변경 시 120ms 디바운스로 미리보기 PNG 갱신
  useEffect(() => {
    let cancelled = false;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      renderCaptionPng(normalized || " ", language, style)
        .then((url) => { if (!cancelled) setPreviewPng(url); })
        .catch(() => { if (!cancelled) setPreviewPng(null); });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [normalized, language, style]);

  const presets = [t.videoCaption.preset1, t.videoCaption.preset2, t.videoCaption.preset3, t.videoCaption.preset4]
    .map((p) => normalizeCaption(p))
    .filter((p) => p.length > 0);

  /** 다음 화면 — 결제가 걸리면 결제부터(영상 생성 직전, BOOTH-61/74/80) */
  const proceed = () => {
    const route = afterCaptionRoute(anyPaymentActive(pay));
    if (route === "payment") beginPayment(true);
    go(route);
  };

  const next = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setRenderError(false);
    try {
      const png = await renderCaptionPng(normalized, language, style);
      setVideoCaption(normalized, png);
      proceed();
    } catch (err) {
      // 캔버스 실패는 드물다 — 자막 없이 진행하되 한 줄 알린다
      console.warn("[VideoCaption] 자막 PNG 렌더 실패 — 자막 없이 진행", err);
      clearVideoCaption();
      setRenderError(true);
      setBusy(false);
      proceed();
    }
  };

  const skip = () => {
    clearVideoCaption();
    proceed();
  };

  const previewStyle = {
    "--caption-ar": videoAspectCss("3:4"),
    "--cap-ratio": String(OVERLAY_RATIO),
  } as React.CSSProperties;

  return (
    <div className="view view--caption view--stackable">
      <div className="view__head">
        <BackButton label={t.videoCaption.back} onClick={() => go(captionBackRoute(selectedMotionIds.length > 0))} />
        <h1 className="view__title">{t.videoCaption.title}</h1>
      </div>
      <p className="view__subtitle">{t.videoCaption.subtitle}</p>

      <div className="view__body caption-body">
        {/* 3:4 미리보기 — 사진 위 하단 1/4 에 1440×480 PNG 오버레이. 결과 mp4 와 같은 비율·배치. .caption-stage 가 size 컨테이너(높이 유도) */}
        <div className="caption-stage">
          <figure className="caption-preview" style={previewStyle} aria-hidden>
            <div className="caption-preview__photo">
              {photo ? <img src={photo} alt="" draggable={false} /> : null}
            </div>
            <div className="caption-overlay">
              {previewPng ? (
                <img className="caption-overlay__png" src={previewPng} alt="" />
              ) : null}
            </div>
          </figure>
        </div>

        <div className="caption-form">
          <div className="caption-field">
            <input
              className="caption-input"
              type="text"
              value={text}
              onChange={(e) => setText(truncateCaption(e.target.value))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void next(); } }}
              placeholder={t.videoCaption.placeholder}
              aria-label={t.videoCaption.title}
              enterKeyHint="done"
              autoCorrect="off"
              autoCapitalize="off"
              autoComplete="off"
              spellCheck={false}
              maxLength={CAPTION_MAX_GRAPHEMES * 8}
              disabled={busy}
            />
            <span className="caption-counter kicker" aria-live="polite">{captionLength(normalized)}/{CAPTION_MAX_GRAPHEMES}</span>
          </div>
          <div className="caption-chips" role="group" aria-label={t.videoCaption.title}>
            {presets.map((p) => (
              <button
                key={p}
                type="button"
                className="caption-chip"
                aria-pressed={normalized === p}
                onClick={() => setText(p)}
                disabled={busy}
              >
                {p}
              </button>
            ))}
          </div>
          {renderError ? <p className="error caption-error">{t.videoGenerate.error}</p> : null}
        </div>
      </div>

      <div className="view__cta actions actions--pair">
        <Button variant="secondary" onClick={skip} disabled={busy}>{t.videoCaption.skip}</Button>
        <Button onClick={() => void next()} disabled={!valid || busy}>{t.videoCaption.next}</Button>
      </div>
    </div>
  );
}

